import { ReviewItemProps } from '@src/quince-components/src'
import { getReviewImageAltText } from '@src/quince-site-pdp/src/atoms/reviewPopUp/utils'

export enum ReviewOptionType {
  CUSTOMER_PROFILE = 'dropdown',
  PRODUCT_PROFILE = 'slider',
}

export enum SummaryReviewOptionType {
  CUSTOMER_PROFILE = 'CUSTOMER_PROFILE',
  PRODUCT_PROFILE = 'PRODUCT_PROFILE',
}

export interface IReviewOption {
  readonly attributeType: ReviewOptionType
  readonly attributeName: string
  readonly translatedAttributeName: string
  readonly displayName: string
  readonly translatedDisplayName: string
  readonly percentage?: number
  readonly value: string
}

export interface ISummaryReviewOption {
  readonly name: string
  readonly translatedName: string
  readonly value: string
  readonly rangePercent?: number
  readonly rangeOptions?: string[]
  readonly translatedRangeOptions?: string[]
  readonly type: SummaryReviewOptionType
}

// Type definition for review tags
export interface IReviewTag {
  readonly tagValues?: string[]
  readonly tags?: string
}

export class ReviewOption implements IReviewOption {
  readonly attributeType: ReviewOptionType
  readonly attributeName: string
  readonly translatedAttributeName: string
  readonly displayName: string
  readonly translatedDisplayName: string
  readonly percentage: number | undefined
  readonly value: string

  constructor({
    attributeType,
    attributeName,
    displayName,
    percentage,
    value,
    translatedAttributeName,
    translatedDisplayName,
  }: IReviewOption) {
    this.attributeType = attributeType
    this.attributeName = attributeName
    this.displayName = displayName
    this.percentage = percentage
    this.value = value
    this.translatedAttributeName = translatedAttributeName
    this.translatedDisplayName = translatedDisplayName
    return {
      ...this,
    }
  }
}

export class SummaryReviewOption implements ISummaryReviewOption {
  readonly name: string
  readonly translatedName: string
  readonly rangeOptions: string[] | undefined
  readonly translatedRangeOptions: string[] | undefined
  readonly rangePercent: number | undefined
  readonly type: SummaryReviewOptionType
  readonly value: string

  constructor({
    name,
    value,
    rangePercent,
    rangeOptions,
    type,
    translatedName,
    translatedRangeOptions,
  }: ISummaryReviewOption) {
    this.name = name
    this.value = value
    this.translatedName = translatedName
    this.rangePercent = rangePercent
    this.rangeOptions = rangeOptions
    this.type = type
    this.translatedRangeOptions = translatedRangeOptions
    return {
      ...this,
    }
  }
}

interface IProductReview {
  id: number
  rating: number
  title: string
  author: string
  productId: string
  productHandle?: string
  createdAt: string
  body: string
  reviewOptions?: IReviewOption[]
  location?: string
  productTitle: string
  contentfulProductHandle?: null | string
  imagesFileUrls?: null | string[]
  source?: string
  reviewTags?: IReviewTag[]
  /** Region ID from API (e.g. 101). Resolved to country display name via RMS for "Reviewed in {country}". */
  regionId?: number
  /** Indicates review was incentivized; when true UI shows reward programme indicator. */
  isIncentivized?: boolean
  /** BE-verified purchase flag; when true UI shows the "Verified purchase" badge (all regions). */
  verifiedPurchase?: boolean
}

export default class ProductReview implements IProductReview {
  readonly id: number
  readonly rating: number
  readonly title: string
  readonly author: string
  readonly productId: string
  readonly productHandle?: string
  readonly createdAt: string
  readonly body: string
  readonly reviewOptions?: IReviewOption[] | undefined
  readonly location?: string
  readonly productTitle: string
  readonly contentfulProductHandle?: string | null | undefined
  readonly imagesFileUrls?: string[] | null | undefined
  readonly source?: string
  readonly reviewTags?: IReviewTag[]
  readonly regionId?: number
  readonly isIncentivized?: boolean
  readonly verifiedPurchase?: boolean

  constructor({
    id,
    rating,
    title,
    author,
    productId,
    productHandle,
    createdAt,
    body,
    reviewOptions,
    location,
    productTitle,
    contentfulProductHandle,
    imagesFileUrls,
    source,
    reviewTags,
    regionId,
    isIncentivized,
    verifiedPurchase,
  }: IProductReview) {
    this.id = id
    this.rating = rating
    this.title = title
    this.author = author
    this.productId = productId
    this.productHandle = productHandle
    this.createdAt = createdAt
    this.body = body
    this.reviewOptions = reviewOptions
    this.location = location
    this.productTitle = productTitle
    this.contentfulProductHandle = contentfulProductHandle
    this.imagesFileUrls = imagesFileUrls
    this.source = source
    this.reviewTags = reviewTags
    this.regionId = regionId
    this.isIncentivized = isIncentivized
    this.verifiedPurchase = verifiedPurchase

    return {
      ...this,
    }
  }

  static getProductReviewPropFromReview = (
    review: ProductReview,
    productTitle = '',
    regionIdToDisplayName?: Record<number, string>,
  ): ReviewItemProps => {
    return {
      reviewId: review.id,
      author: review.author,
      date: review.createdAt,
      reviewOptions: review.reviewOptions,
      rating: review.rating,
      title: review.title,
      body: review.body,
      productTitle: review.productTitle,
      reviewImageProps: review.imagesFileUrls?.map(fileUrl => {
        return {
          url: fileUrl,
          title: productTitle,
          alt: getReviewImageAltText({ reviewTitle: review.title, productTitle: productTitle }),
        }
      }),
      source: review.source,
      brandName: review.reviewTags?.filter(x => x.tags === 'EXTERNAL_BRAND_NAME')?.[0]?.tagValues?.join(', '),
      reviewedInCountry: review.regionId != null ? regionIdToDisplayName?.[review.regionId] : undefined,
      isIncentivized: review.isIncentivized,
      verifiedPurchase: review.verifiedPurchase,
    }
  }
}

interface IProductReviewSummary {
  readonly reviewCount: number
  readonly averageRating: number
  readonly spid?: number
  readonly reviewOptions?: ISummaryReviewOption[]
}

export class ProductReviewSummary implements IProductReviewSummary {
  readonly reviewCount: number
  readonly averageRating: number
  readonly spid?: number
  readonly reviewOptions: SummaryReviewOption[] | undefined
  readonly totalCount?: number | undefined

  constructor({ reviewCount, averageRating, spid, reviewOptions }: IProductReviewSummary) {
    this.reviewCount = reviewCount
    this.averageRating = averageRating
    this.spid = spid
    this.reviewOptions = reviewOptions?.map(ro => new SummaryReviewOption(ro))
    return {
      ...this,
    }
  }
}
